import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { IconType } from '~/eds/src/types/IconType';
import { api } from '~/redux/api/createApi';
import chatbot from '~/redux/slices/chatbot';

import { MessageAction, Message as MessageType } from '../types/chat';

const DEFAULT_TIMEOUT = 3000;

interface UseGetUserMessageActionsProps {
  scope: 'document_handler' | 'document_version' | 'all_documents';
  options?: {
    timeout?: number;
  };
}

export const useGetUserMessageActions = ({
  scope,
  options = {},
}: UseGetUserMessageActionsProps): ({
  resolveIcon?: (message: MessageType) => IconType;
} & MessageAction)[] => {
  const intl = useIntl();
  const [savedQuestionIds, setSavedQuestionIds] = useState<string[]>([]);
  const [createQuestion, { isLoading }] =
    api.endpoints.createQuestion.useMutation();
  const { toast } = useToast();

  // redux
  const dispatch = useDispatch();

  const openSideContent = (
    tab?: 'conversationHistory' | 'questions' | 'questionGroups',
  ) => {
    dispatch(chatbot.actions.openSideContent(tab));
  };

  // clear saved question ids after 3000ms so the icon will change
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (savedQuestionIds.length) {
      timeout = setTimeout(() => {
        setSavedQuestionIds?.([]);
      }, options?.timeout ?? DEFAULT_TIMEOUT);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [savedQuestionIds]);

  const handleOnClick = useCallback(
    async (message: MessageType) => {
      if (!scope) {
        toast({
          message: intl.formatMessage({
            id: 'UseGetUserMessageActions.scopeRequiredError',
            defaultMessage:
              'A scope must be selected in order to save a question.',
            description:
              'Error message when no scope is selected for saving a question',
          }),
          status: 'danger',
        });
      }
      try {
        await createQuestion({ text: message.text, scope }).unwrap();
        setSavedQuestionIds((prev) => [...prev, message.id]);
        toast({
          message: intl.formatMessage({
            id: 'UseGetUserMessageActions.questionSavedSuccess',
            defaultMessage: 'Question was successfully saved.',
            description: 'Success message when a question is saved',
          }),
          status: 'success',
          action: {
            text: intl.formatMessage({
              id: 'UseGetUserMessageActions.viewAction',
              defaultMessage: 'View',
              description: 'Action button text to view saved questions',
            }),
            level: 'action',
            onClick: () => {
              openSideContent('questions');
            },
          },
        });
      } catch (apiError: any) {
        // TODO: find a better way to show all errors
        const error = apiError.errors[0];
        toast({
          message: intl.formatMessage(
            {
              id: 'UseGetUserMessageActions.questionSaveError',
              defaultMessage:
                'Question was not able to be saved due to {errorTitle}.',
              description:
                'Error message when saving a question fails. Example: errorTitle = "Network Error", "Validation Error"',
            },
            {
              errorTitle: error.title,
            },
          ),
          status: 'danger',
        });
      }
    },
    [scope],
  );

  const resolveIcon = useCallback(
    (message: MessageType): IconType => {
      return savedQuestionIds.includes(message.id)
        ? 'bookmark-filled'
        : 'bookmark';
    },
    [savedQuestionIds],
  );

  const userAction = useMemo(
    () => [
      {
        tooltip: isLoading
          ? intl.formatMessage({
              id: 'UseGetUserMessageActions.savingQuestionTooltip',
              defaultMessage: 'Saving Question',
              description: 'Tooltip text when a question is being saved',
            })
          : intl.formatMessage({
              id: 'UseGetUserMessageActions.saveQuestionTooltip',
              defaultMessage: 'Save question',
              description: 'Tooltip text for save question button',
            }),
        icon: 'bookmark' as const,
        isLoading: isLoading,
        resolveIcon,
        onClick: handleOnClick,
      },
    ],
    [isLoading, resolveIcon, handleOnClick],
  );

  return userAction;
};
